import { useState } from "react";
import { Link } from "react-router-dom";
import type { Product } from "@/mocks/products";
import { useCart } from "@/context/CartContext";
import { formatBRL } from "@/utils/format";
import { discountPercent } from "@/utils/collections";
import { colorAttribute, colorHex, defaultVariant, sizeAttribute, variantForChoice, variantImage, variantLabel, variantMaxQuantity } from "@/utils/variants";

// Cartão de produto: foto, nome, preço e variações sempre à vista. A cor define a
// variação e o clique no tamanho coloca a peça na sacola.
export default function ProductCard({ product }: { product: Product }) {
  const { addItem, openCart } = useCart();
  const href = `/produto/${product.id}`;
  const off = discountPercent(product);
  const soldOut = product.inStock === false;
  const sizeName = sizeAttribute(product);
  const colorName = colorAttribute(product);
  const sizes = sizeName ? product.attributes?.[sizeName] ?? [] : [];
  const colors = colorName ? product.attributes?.[colorName] ?? [] : [];
  const [color, setColor] = useState(() => (colorName ? defaultVariant(product)?.values[colorName] : undefined) ?? colors[0]);
  const choice = (size: string) => (sizeName ? variantForChoice(product, sizeName, size, colorName, color) : undefined);
  const photo = variantImage(product, sizes[0] ? choice(sizes[0]) : undefined);
  const secondImage = product.images?.find((image) => image !== photo);

  const addSize = (size: string) => {
    const variant = choice(size);
    if (!variant) return;
    addItem({
      id: product.id,
      name: product.name,
      price: variant.price,
      image: variantImage(product, variant),
      variantId: variant.id,
      variantLabel: variantLabel(variant.values),
      maxQuantity: variantMaxQuantity(variant),
    });
    openCart();
  };

  return (
    <article className="bz-card">
      <div className="bz-card-media">
        <Link to={href} className="bz-card-photo" aria-label={product.name}>
          <img src={photo} alt={product.name} loading="lazy" decoding="async" />
          {secondImage && <img className="bz-card-alt" src={secondImage} alt="" loading="lazy" decoding="async" />}
        </Link>
        {soldOut ? <span className="bz-tag">Esgotado</span> : off > 0 && <span className="bz-tag is-sale">-{off}%</span>}
      </div>

      <div className="bz-card-info">
        <h3><Link to={href}>{product.name}</Link></h3>

        <p className="bz-card-price">
          {off > 0 && product.compareAtPrice && <s>{formatBRL(product.compareAtPrice)}</s>}
          <strong>{formatBRL(product.price)}</strong>
        </p>
        <p className="bz-card-terms">{product.installments}x de {formatBRL(product.installmentValue)}</p>

        {colors.length > 0 && (
          <div className="bz-card-colors" role="radiogroup" aria-label={`Cores de ${product.name}`}>
            {colors.slice(0, 6).map((name) => {
              const hex = colorHex(name);
              return hex ? (
                <button key={name} type="button" role="radio" aria-checked={name === color} aria-label={name} title={name} style={{ background: hex }} onClick={() => setColor(name)} />
              ) : (
                <button key={name} type="button" role="radio" aria-checked={name === color} className="is-text" onClick={() => setColor(name)}>{name}</button>
              );
            })}
            {colors.length > 6 && <Link to={href}>+{colors.length - 6}</Link>}
          </div>
        )}

        {sizes.length > 0 && !soldOut && (
          <div className="bz-card-sizes" role="group" aria-label={`Adicionar à sacola: tamanhos de ${product.name}`}>
            {sizes.map((size) => (
              <button key={size} type="button" disabled={!choice(size)} title={`Adicionar tamanho ${size}`} aria-label={`Adicionar ${product.name}, tamanho ${size}${color ? `, cor ${color}` : ""}`} onClick={() => addSize(size)}>
                {size}
              </button>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}
